import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { AgeGate } from './AgeGate';

const AGE_CONFIRMED_KEY = 'ws_age_confirmed';
const AGE_DECLINED_KEY = 'ws_age_declined';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

interface AgeGateContextValue {
  isConfirmed: boolean;
  isDeclined: boolean;
}

const AgeGateContext = createContext<AgeGateContextValue>({
  isConfirmed: false,
  isDeclined: false,
});

export function useAgeGate() {
  return useContext(AgeGateContext);
}

function isConfirmationValid(): boolean {
  try {
    const stored = localStorage.getItem(AGE_CONFIRMED_KEY);
    if (!stored) return false;
    const confirmationTime = new Date(stored).getTime();
    if (Number.isNaN(confirmationTime)) return false;
    const elapsed = Date.now() - confirmationTime;
    return elapsed >= 0 && elapsed < THIRTY_DAYS_MS;
  } catch {
    return false;
  }
}

function isDeclinedForSession(): boolean {
  try {
    return sessionStorage.getItem(AGE_DECLINED_KEY) === 'true';
  } catch {
    return false;
  }
}

interface AgeGateProviderProps {
  children: ReactNode;
}

export function AgeGateProvider({ children }: AgeGateProviderProps) {
  const [isConfirmed, setIsConfirmed] = useState(() => isConfirmationValid());
  const [isDeclined, setIsDeclined] = useState(() => isDeclinedForSession());

  // Re-check confirmation validity on mount (handles 24h expiry on reload)
  useEffect(() => {
    setIsConfirmed(isConfirmationValid());
    setIsDeclined(isDeclinedForSession());
  }, []);

  const handleConfirm = useCallback(() => {
    const timestamp = new Date().toISOString();
    localStorage.setItem(AGE_CONFIRMED_KEY, timestamp);
    setIsConfirmed(true);
  }, []);

  const handleDecline = useCallback(() => {
    sessionStorage.setItem(AGE_DECLINED_KEY, 'true');
    setIsDeclined(true);
  }, []);

  const showGate = !isConfirmed;

  return (
    <AgeGateContext.Provider value={{ isConfirmed, isDeclined }}>
      {showGate && (
        <AgeGate
          isDeclined={isDeclined}
          onConfirm={handleConfirm}
          onDecline={handleDecline}
        />
      )}
      {!showGate && children}
    </AgeGateContext.Provider>
  );
}
